import type {PropertyValuesRecord, Repository} from '@github-ui/custom-properties-types'
import {human} from '@github-ui/formatters'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {repoOverviewUrl} from '@github-ui/paths'
import {type Icon, PencilIcon, RepoIcon, RepoLockedIcon} from '@primer/octicons-react'
import {ActionList, Button, IconButton, Pagination, Spinner, Truncate} from '@primer/react'
import {useCallback, useMemo} from 'react'

import styles from './ReposPropertiesList.module.css'

export function ReposPropertiesList({
  orgName,
  selectedRepoIds,
  repositoryCount,
  repos,
  pageCount,
  page,
  showSpinner,
  onSelectionChange,
  onPageChange,
  onEditRepoPropertiesClick,
}: {
  orgName: string
  selectedRepoIds: Set<number>
  repositoryCount: number
  repos: Repository[]
  pageCount: number
  page: number
  showSpinner: boolean
  onPageChange: (page: number) => void
  onSelectionChange: (selectedIds: Set<number>) => void
  onEditRepoPropertiesClick: (repositorySelections: Repository[], elementId: string) => void
}) {
  const onRepoSelectToggle = useCallback(
    (repo: Repository) => {
      const update = new Set(selectedRepoIds)
      if (selectedRepoIds.has(repo.id)) {
        update.delete(repo.id)
        onSelectionChange(update)
      } else {
        update.add(repo.id)
        onSelectionChange(update)
      }
    },
    [selectedRepoIds, onSelectionChange],
  )

  const selectedRepos = useMemo(() => repos.filter(repo => selectedRepoIds.has(repo.id)), [repos, selectedRepoIds])
  const hasSelectedRepos = selectedRepos.length > 0

  const metadata = (
    <ListViewMetadata
      onToggleSelectAll={isSelectAllChecked => {
        return isSelectAllChecked
          ? onSelectionChange(new Set(repos.map(repo => repo.id)))
          : onSelectionChange(new Set())
      }}
      title={<span className={styles.repositoriesCountText}>{reposCountLabel(repositoryCount)}</span>}
      actionsLabel="Actions"
      actions={[
        {
          key: 'edit',
          render(isOverflowMenu) {
            if (!hasSelectedRepos) {
              return <></>
            }

            return isOverflowMenu ? (
              <ActionList.Item
                id="button:edit-multiple-repos"
                onSelect={() => onEditRepoPropertiesClick(selectedRepos, 'button:edit-multiple-repos')}
              >
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit properties
              </ActionList.Item>
            ) : (
              <Button
                id="button:edit-multiple-repos"
                leadingVisual={PencilIcon}
                onClick={() => onEditRepoPropertiesClick(selectedRepos, 'button:edit-multiple-repos')}
              >
                Edit properties
              </Button>
            )
          },
        },
      ]}
    >
      {showSpinner && <Spinner size="small" className={styles.loadingSpinner} />}
    </ListViewMetadata>
  )

  return (
    <>
      <div data-testid="repos-properties-list" className={styles.repositoriesListContainer}>
        <ListView
          isSelectable
          variant="compact"
          metadata={metadata}
          title="Repositories"
          titleHeaderTag="h2"
          singularUnits="repository"
          pluralUnits="repositories"
          totalCount={repos.length}
        >
          {repos.map(repo => (
            <ReposPropertiesListItem
              key={repo.id}
              {...repo}
              orgName={orgName}
              selected={selectedRepoIds.has(repo.id)}
              onEditButtonClick={buttonId => onEditRepoPropertiesClick([repo], buttonId)}
              onSelect={() => onRepoSelectToggle(repo)}
            />
          ))}
        </ListView>
      </div>
      <Pagination
        pageCount={pageCount}
        currentPage={page}
        onPageChange={(e, newPage) => {
          // Avoid adding hash parameters
          e.preventDefault()

          window.scrollTo({top: 0, behavior: 'smooth'})
          onPageChange(newPage)
        }}
      />
    </>
  )
}

export function ReposPropertiesListItem({
  name,
  orgName,
  description,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  properties = {},
  selected,
  visibility,
  onEditButtonClick,
  onSelect,
}: {
  name: string
  orgName: string
  description: string | null
  properties?: PropertyValuesRecord
  visibility: Repository['visibility']
  onEditButtonClick: (elementId: string) => void
  selected: boolean
  onSelect: () => void
}) {
  const buttonId = `list-item:button:edit-repo-${name}`
  const propertiesLength = Object.keys(properties).length

  return (
    <ListItem
      title={<ListItemTitle href={repoOverviewUrl({name, ownerLogin: orgName})} value={name} />}
      onSelect={onSelect}
      isSelected={selected}
      metadata={
        <>
          <ListItemMetadata alignment="right" className={styles.propertiesCountMetadata}>
            <span>
              {propertiesLength
                ? `${propertiesLength} ${propertiesLength === 1 ? 'property' : 'properties'}`
                : 'No properties'}
            </span>
          </ListItemMetadata>
          <ListItemMetadata alignment="right" variant="primary" className={styles.editButtonMetadata}>
            <IconButton
              id={buttonId}
              icon={PencilIcon}
              variant="invisible"
              aria-label={`Edit ${name}'s properties`}
              onClick={() => onEditButtonClick(buttonId)}
              description="Edit properties"
            />
          </ListItemMetadata>
        </>
      }
      metadataContainerClassName={styles.listItemWithRightPadding}
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual color="fg.muted" icon={visibilityMap[visibility]} />
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription className={styles.repositoryDescription}>
          {description && (
            <Truncate inline title={description} className={styles.descriptionTruncate}>
              {description}
            </Truncate>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

const visibilityMap: Record<Repository['visibility'], Icon> = {
  private: RepoLockedIcon,
  public: RepoIcon,
  internal: RepoIcon,
}

function reposCountLabel(reposCount: number) {
  return `${human(reposCount)} ${reposCount === 1 ? 'repository' : 'repositories'}`
}

try{ ReposPropertiesList.displayName ||= 'ReposPropertiesList' } catch {}
try{ ReposPropertiesListItem.displayName ||= 'ReposPropertiesListItem' } catch {}